// System (Redesign Schritt 6, Spec 4.5): Status aus dem Watchdog + Uebersicht, Nachschlagen, Abmelden
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { useSubTab } from "../shell/SubTabs";
import { ARCHITECTURE, AUTO_WINDOWS, CHANGELOG, INTRADAY, LONGTERM, MARKETS, MODE_META, SWING, Strategy } from "../system/reference";

const fmtTs = (iso: string | null | undefined, withDate = true) => {
  if (!iso) return "–";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("de-DE", { timeZone: "Europe/Berlin", ...(withDate ? { day: "2-digit", month: "2-digit" } : {}), hour: "2-digit", minute: "2-digit" }).replace(",", "");
};
const ago = (iso: string | null | undefined) => {
  if (!iso) return null;
  const m = (Date.now() - new Date(iso).getTime()) / 6e4;
  if (isNaN(m)) return null;
  return m < 90 ? `vor ${Math.round(m)} Min` : m < 48 * 60 ? `vor ${Math.round(m / 60)} Std` : `vor ${Math.round(m / 1440)} Tagen`;
};
// systemd liefert z. B. "Mon 2026-10-19 06:52:03 UTC"
const sinceText = (s: string | null) => {
  if (!s) return "–";
  const m = s.match(/(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}) (\w+)/);
  if (!m) return s;
  const d = new Date(`${m[1]}T${m[2]}${m[3] === "UTC" ? "Z" : ""}`);
  return isNaN(d.getTime()) ? s : `seit ${fmtTs(d.toISOString())}`;
};

type Row = { name: string; sub: string; state: string; good: boolean | null; mono?: boolean };
function Section({ title, rows }: { title: string; rows: Row[] }) {
  if (!rows.length) return null;
  return (
    <section className="sy-sec">
      <h3>{title}</h3>
      {rows.map(r => (
        <div key={r.name} className="sy-row">
          <span className={`sy-dot ${r.good === null ? "" : r.good ? "ok" : "warn"}`} aria-hidden="true" />
          <div className="sy-body"><div className={r.mono ? "mono" : ""}>{r.name}</div><div className="sy-sub">{r.sub}</div></div>
          <span className={`sy-state ${r.good === false ? "warn" : ""}`}>{r.state}</span>
        </div>
      ))}
    </section>
  );
}

function Status() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["system-overview"],
    queryFn: () => apiClient.get("/monitoring/overview").then(r => r.data),
    refetchInterval: 60_000,
  });
  if (isLoading) return <div className="sc-empty">Lädt …</div>;
  if (isError || !data) return <div className="sc-empty">Status konnte nicht geladen werden.</div>;
  const wd = data.watchdog || {}, checks: Record<string, any> = wd.checks || {};
  const chk = (k: string) => checks[k];
  const fails = Object.entries(checks).filter(([, v]) => v?.status === "fail");

  const services: Row[] = (data.services || []).map((s: any) => {
    const extra = s.name === "trading-scanner" && wd.last_scan_cycle ? ` · letzter Zyklus ${fmtTs(wd.last_scan_cycle, false)}`
      : s.name === "trading-scheduler" && wd.last_intraday_fetch ? ` · letzter Fetch ${fmtTs(wd.last_intraday_fetch, false)}` : "";
    return { name: s.name, sub: sinceText(s.since) + extra, state: s.state === "active" ? "aktiv" : s.state, good: s.state === "active", mono: true };
  });
  const regionKey: Record<string, string> = { Asien: "daily:Asien", EU: "daily:EU", US: "daily:US" };
  const dataRows: Row[] = (data.data || []).map((d: any) => {
    const c = chk(regionKey[d.region]);
    const days = d.daily ? (Date.now() - new Date(d.daily).getTime()) / 864e5 : null;
    const good = c ? c.status === "ok" : days !== null ? days <= 4 : false;   // ohne Watchdog-Check: Wochenende einrechnen
    return { name: `${d.region}`, sub: `Daily ${d.daily ? d.daily.slice(8, 10) + "." + d.daily.slice(5, 7) + "." : "–"} · 15 Min ${fmtTs(d.min15)}`,
             state: d.error ? "Fehler" : good ? "aktuell" : "veraltet", good: d.error ? false : good };
  });
  if (data.earnings_fetched !== undefined)
    dataRows.push({ name: "Earnings", sub: data.earnings_fetched ? `abgerufen ${fmtTs(data.earnings_fetched)}` : "kein Abruf gefunden",
                    state: data.earnings_fetched ? (ago(data.earnings_fetched) || "") : "–", good: !!data.earnings_fetched && (Date.now() - new Date(data.earnings_fetched).getTime()) < 4 * 864e5 });
  const jobs: Row[] = [
    ["backup:local", "DB-Backup lokal"], ["backup:offsite", "Offsite-Backup"],
    ["res:disk", "Platte"], ["res:ram", "Arbeitsspeicher"], ["res:swap", "Swap"],
  ].filter(([k]) => chk(k)).map(([k, label]) => ({ name: label, sub: chk(k).text || "", state: chk(k).status === "ok" ? "ok" : "prüfen", good: chk(k).status === "ok" }));
  Object.entries(checks).filter(([k]) => k.startsWith("cert:")).forEach(([k, v]) =>
    jobs.push({ name: `Zertifikat ${k.slice(5)}`, sub: v.text || "", state: v.status === "ok" ? "ok" : "prüfen", good: v.status === "ok" }));
  const other: Row[] = ["api:http", "scanner:cycle", "fetch:intraday"].filter(k => chk(k))
    .map(k => ({ name: ({ "api:http": "API", "scanner:cycle": "Scanner-Zyklus", "fetch:intraday": "Intraday-Fetch" } as any)[k],
                 sub: chk(k).text || "", state: chk(k).status === "ok" ? "ok" : "prüfen", good: chk(k).status === "ok" }));
  const nHints = fails.length + dataRows.filter(r => r.good === false).length;
  const top = fails[0]?.[1]?.text || dataRows.find(r => r.good === false)?.sub;

  return (
    <>
      <div className={`sy-head ${nHints ? "warn" : "ok"}`} role="status">
        <span className={`sy-dot ${nHints ? "warn" : "ok"}`} aria-hidden="true" />
        <div className="sy-body">
          <div className="sy-title">{nHints ? `${nHints} Hinweis${nHints === 1 ? "" : "e"}` : "Alles ok"}</div>
          {nHints > 0 && top && <div className="sy-sub">{nHints === 1 ? top : `${top} · und weitere`}</div>}
          <div className="sy-sub">Watchdog geprüft {wd.checks && Object.values(checks)[0]?.checked ? fmtTs(Object.values(checks)[0].checked) : "–"}</div>
        </div>
        <button className="jf-btn" onClick={() => refetch()} disabled={isFetching}>{isFetching ? "…" : "Neu laden"}</button>
      </div>
      <Section title="Dienste" rows={services} />
      <Section title="Abläufe" rows={other} />
      <Section title="Datenstand" rows={dataRows} />
      <Section title="Backups & Server" rows={jobs} />
    </>
  );
}

// ── Nachschlagen ──
const PAGES = [
  { key: "strategien", label: "Strategien" },
  { key: "zeiten", label: "Märkte & Auto-Zeiten" },
  { key: "changelog", label: "Changelog" },
  { key: "architektur", label: "Architektur" },
] as const;
type PageKey = typeof PAGES[number]["key"];

function Strategies() {
  const [mode, setMode] = useState<"swing" | "longterm" | "intraday">("swing");
  const [open, setOpen] = useState<string | null>(null);
  const list: Strategy[] = mode === "swing" ? SWING : mode === "longterm" ? LONGTERM : INTRADAY;
  const meta = MODE_META[mode];
  return (
    <>
      <div className="sc-modes">{(["swing", "longterm", "intraday"] as const).map(m =>
        <button key={m} className={`nx-subtab${mode === m ? " active" : ""}`} onClick={() => setMode(m)}>{m === "swing" ? "Swing" : m === "longterm" ? "Longterm" : "Intraday"}</button>)}</div>
      <p className="au-note mono">{meta.threshold} · SL {meta.sl} · TP {meta.tp} · RRR {meta.rrr}</p>
      <div className="j-list">
        {list.map(s => (
          <div key={s.name} className="j-item">
            <div className="j-row" role="button" tabIndex={0} aria-expanded={open === s.name} onClick={() => setOpen(open === s.name ? null : s.name)}>
              <div className="sc-body"><div className="sc-name"><span className="sc-company">{s.label}</span>{s.cluster && <span className="j-product">{s.cluster}</span>}</div>
                <div className="sc-time">{s.short}</div></div>
            </div>
            {open === s.name && (
              <dl className="jd jd-kv sy-kv">
                <div><dt>Beschreibung</dt><dd>{s.description}</dd></div>
                <div><dt>Berechnung</dt><dd className="mono">{s.calc}</dd></div>
                {s.entry && <div><dt>Einstieg</dt><dd>{s.entry}</dd></div>}
                <div><dt>Zeitrahmen</dt><dd>{s.timeframe}</dd></div>
              </dl>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function Reference({ page }: { page: PageKey }) {
  if (page === "strategien") return <Strategies />;
  if (page === "zeiten") return (
    <>
      <section className="sy-sec"><h3>Auto-Auswahl im Scanner (Berlin)</h3>
        {AUTO_WINDOWS.map(w => <div key={w.time} className="au-line"><span className="mono">{w.time}</span><span>{w.markets}</span></div>)}</section>
      <section className="sy-sec"><h3>Abgedeckte Märkte</h3>
        {MARKETS.map(m => <div key={m.region} className="au-line"><span>{m.region}</span><span className="sy-right">{m.detail}</span></div>)}</section>
    </>
  );
  if (page === "architektur") return <section className="sy-sec">{ARCHITECTURE.map(a => <div key={a.label} className="au-line"><span>{a.label}</span><span className="sy-right">{a.value}</span></div>)}</section>;
  return (
    <>
      {CHANGELOG.map((c: any) => (
        <section key={c.version} className="sy-sec">
          <h3><span className="mono">v{c.version}</span> <span className="sy-sub">{c.date}</span></h3>
          {c.items.map((it: string, i: number) => <p key={i} className="au-hint">{it}</p>)}
        </section>
      ))}
    </>
  );
}

const KEYS = ["status", ...PAGES.map(p => p.key)] as const;

export default function SystemHub({ onLogout }: { onLogout: () => void }) {
  const [tab, setTab] = useSubTab(KEYS as unknown as readonly string[], "status");
  const page = PAGES.find(p => p.key === tab);
  return (
    <>
      {page ? (
        <>
          <button className="sy-back" onClick={() => setTab("status")}>‹ System</button>
          <h2 className="sy-h2">{page.label}</h2>
          <Reference page={page.key} />
        </>
      ) : (
        <>
          <Status />
          <section className="sy-sec">
            <h3>Nachschlagen</h3>
            {PAGES.map(p => <button key={p.key} className="sy-link" onClick={() => setTab(p.key)}><span>{p.label}</span><span aria-hidden="true">›</span></button>)}
          </section>
          <button className="j-add" onClick={onLogout}>Abmelden</button>
        </>
      )}
    </>
  );
}
